/**
 * Main Layout Component
 *
 * The primary layout wrapper for authenticated pages.
 * Contains the sidebar, header, and main content area.
 */

import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';

import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { cn } from '@/utils/cn';
import { getSidebarExpanded, setSidebarExpanded } from '@/utils/storage.utils';

export function MainLayout() {
  // Sidebar state - persisted in localStorage
  const [sidebarExpanded, setSidebarExpandedState] = useState(() => getSidebarExpanded());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Persist sidebar state
  const handleSidebarToggle = () => {
    const newState = !sidebarExpanded;
    setSidebarExpandedState(newState);
    setSidebarExpanded(newState);
  };

  // Close mobile menu on window resize (when switching to desktop)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <div className="flex h-screen overflow-hidden bg-surface dark:bg-background-dark">
      {/* Sidebar */}
      <Sidebar
        expanded={sidebarExpanded}
        onToggle={handleSidebarToggle}
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
      />

      {/* Main content area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <Header onMobileMenuOpen={() => setMobileMenuOpen(true)} />

        {/* Page content */}
        <main
          className={cn(
            'flex-1 overflow-y-auto',
            'p-4 sm:p-6',
            'transition-all duration-300'
          )}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
